import { apiFetch } from './http';
import { putFileToSignedUrl, sha256Base64, uploadFileName } from './attachments';
import { buildPortalApiPath } from './routes';

export type PortalAccess = 'granted' | 'denied';

// What a session call answers. cardId is the Copay Card ID of the escalation the link belongs to,
// shown read-only on the page. It is absent when the API could not load one. Keep it in memory
// only: never in the URL or in browser storage.
export interface PortalSession {
  access: PortalAccess;
  cardId?: string;
}

// Thrown for failures worth retrying (network errors, 5xx). A refusal is not an error: the API
// answers every invalid, expired or revoked request the same way.
export class PortalUnavailableError extends Error {}

async function portalSessionRequest(init: RequestInit): Promise<PortalSession> {
  let res: Response;
  try {
    res = await apiFetch(buildPortalApiPath('/session'), init);
  } catch {
    throw new PortalUnavailableError('network error');
  }
  if (res.status === 200) {
    let body: { cardId?: unknown };
    try {
      body = await res.json() as { cardId?: unknown };
    } catch {
      throw new PortalUnavailableError('unreadable session answer');
    }
    return { access: 'granted', cardId: typeof body?.cardId === 'string' && body.cardId ? body.cardId : undefined };
  }
  // 404 is the API's generic refusal; 403 is a WAF block (geo or rate limit), which retrying won't fix.
  if (res.status === 404 || res.status === 403) return { access: 'denied' };
  throw new PortalUnavailableError(`unexpected status ${res.status}`);
}

const jsonPost = (body: unknown): RequestInit => ({
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

// Trades the invitation secret from the link for a session cookie. The secret goes in the body,
// never in a URL.
export const exchangeInvitation = (key: string) => portalSessionRequest(jsonPost({ k: key }));

export const checkPortalSession = () => portalSessionRequest({ method: 'GET' });

// Why a portal upload call failed. The API gives one fixed answer per kind, so the page can
// choose a message without learning anything about the case.
export type PortalUploadErrorKind = 'denied' | 'invalid' | 'notAccepted' | 'unavailable';

export class PortalUploadError extends Error {
  kind: PortalUploadErrorKind;

  constructor(kind: PortalUploadErrorKind) {
    super(`portal upload ${kind}`);
    this.name = 'PortalUploadError';
    this.kind = kind;
  }
}

export interface PortalConfirmation {
  reference: string;
  submittedAt: string;
  files: string[];
}

interface PortalReservation {
  attachmentId: string;
  uploadUrl?: string;
  headers?: Record<string, string>;
  mock?: boolean;
  finalized?: boolean;
}

function isAbort(err: unknown): boolean {
  return err instanceof DOMException && err.name === 'AbortError';
}

// 404 is the API's generic refusal and 403 a WAF block; both mean the session cannot continue.
async function portalUploadRequest(path: `/${string}`, body: unknown, signal: AbortSignal): Promise<Response> {
  let res: Response;
  try {
    res = await apiFetch(buildPortalApiPath(path), { ...jsonPost(body), signal });
  } catch (err) {
    if (isAbort(err)) throw err;
    throw new PortalUploadError('unavailable');
  }
  if (res.ok) return res;
  if (res.status === 404 || res.status === 403) throw new PortalUploadError('denied');
  if (res.status === 400) throw new PortalUploadError('invalid');
  if (res.status === 409) throw new PortalUploadError('notAccepted');
  throw new PortalUploadError('unavailable');
}

const EXTENSION_CONTENT_TYPES: Record<string, string> = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
};

// Some browsers report an empty type; fall back to the extension, as the file picker does.
function portalContentType(file: File): string {
  if (file.type) return file.type;
  const ext = file.name.includes('.') ? file.name.split('.').pop()?.toLowerCase() : undefined;
  return (ext && EXTENSION_CONTENT_TYPES[ext]) || '';
}

// Uploads one file with the same three steps as the escalation status page: reserve with the
// API, send the bytes straight to BPS storage, then finalize. Calling it again with the same
// idempotency key is safe; a file that is already finalized is answered without sending bytes.
export async function uploadPortalFile(submissionKey: string, file: File, idempotencyKey: string, signal: AbortSignal): Promise<void> {
  const reserved = await portalUploadRequest('/uploads', {
    submissionKey,
    idempotencyKey,
    fileName: uploadFileName(file),
    fileSizeBytes: file.size,
    contentType: portalContentType(file),
    sha256: await sha256Base64(file),
  }, signal);
  const reservation = await reserved.json() as PortalReservation;
  if (reservation.finalized) return;

  if (!reservation.mock) {
    if (!reservation.uploadUrl) throw new PortalUploadError('unavailable');
    try {
      await putFileToSignedUrl(reservation.uploadUrl, reservation.headers, file, signal);
    } catch (err) {
      if (isAbort(err)) throw err;
      throw new PortalUploadError('unavailable');
    }
  }
  await portalUploadRequest(`/uploads/${encodeURIComponent(reservation.attachmentId)}/finalize`, {}, signal);
}

export async function completePortalSubmission(submissionKey: string, receiptRequested: boolean, signal: AbortSignal): Promise<PortalConfirmation> {
  const res = await portalUploadRequest('/submissions', { submissionKey, receiptRequested }, signal);
  return res.json() as Promise<PortalConfirmation>;
}
